import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Platform,
  Alert,
  Animated,
  Dimensions,
} from 'react-native';
import { Image } from 'expo-image';
import { getApiUrl } from '../utils/apiConfig';
import { dedupeCoursesForDropdown } from '../utils/coursesCatalog';
import {
  exercisesListUrl,
  exerciseByIdUrl,
  exerciseTypesByCourseUrl,
  nonCourseExerciseTypesUrl,
} from '../utils/exerciseApi';
import { attachExerciseWithWeightDefaults } from '../utils/sessionExercisePerformance';

const COURSES_IMAGE = require('../../assets/ChatGPT Image Jan 1, 2026, 02_28_17 AM.png');
const EXERCISES_IMAGE = require('../../assets/ChatGPT Image Jan 20, 2026, 08_11_59 AM.png');
const GOALS_IMAGE = require('../../assets/ChatGPT Image Mar 13, 2026, 08_37_09 PM.png');
const { width } = Dimensions.get('window');

function flashTileRed(animRef) {
  Animated.sequence([
    Animated.timing(animRef, { toValue: 0, duration: 0, useNativeDriver: false }),
    Animated.timing(animRef, { toValue: 1, duration: 1500, useNativeDriver: false }),
  ]).start();
}

export default function RoutineExercisePicker({ theme, studentId, onExercisesAdded }) {
  const styles = getStyles(theme);
  const coursesTileColorAnim = useRef(new Animated.Value(1)).current;
  const exercisesTileColorAnim = useRef(new Animated.Value(1)).current;
  const goalsTileColorAnim = useRef(new Animated.Value(1)).current;

  const [courses, setCourses] = useState([]);
  const [showCoursesDropdown, setShowCoursesDropdown] = useState(false);
  const [showExercisesMenu, setShowExercisesMenu] = useState(false);
  const [showGradeExercises, setShowGradeExercises] = useState(false);
  const [gradeExercises, setGradeExercises] = useState([]);
  const [exercisePickerSource, setExercisePickerSource] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [exerciseTypes, setExerciseTypes] = useState([]);
  const [showExerciseTypesDropdown, setShowExerciseTypesDropdown] = useState(false);
  const [selectedExerciseType, setSelectedExerciseType] = useState(null);
  const [exercises, setExercises] = useState([]);
  const [showExercisesDropdown, setShowExercisesDropdown] = useState(false);
  const [selectedExercises, setSelectedExercises] = useState(new Set());
  const [selectAllExercises, setSelectAllExercises] = useState(false);
  const [showGoalsDropdown, setShowGoalsDropdown] = useState(false);
  const [goals, setGoals] = useState([]);
  const [loadingGoals, setLoadingGoals] = useState(false);
  const [expandedGoalId, setExpandedGoalId] = useState(null);

  useEffect(() => {
    fetch(`${getApiUrl()}/api/courses`)
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => setCourses(dedupeCoursesForDropdown(Array.isArray(data) ? data : [])))
      .catch(() => setCourses([]));
  }, []);

  const subMenuActive =
    selectedExerciseType || showExercisesDropdown || showExerciseTypesDropdown;

  const closeOtherMenus = useCallback((except) => {
    if (except !== 'courses') setShowCoursesDropdown(false);
    if (except !== 'exercises') {
      setShowExercisesMenu(false);
      setShowGradeExercises(false);
    }
    if (except !== 'goals') setShowGoalsDropdown(false);
    if (except !== 'types') setShowExerciseTypesDropdown(false);
  }, []);

  const mergeAndNotify = useCallback(
    async (rawExercises) => {
      const list = Array.isArray(rawExercises) ? rawExercises : [rawExercises];
      const withPerformance = await Promise.all(
        list.map((ex) => attachExerciseWithWeightDefaults(ex, studentId))
      );
      onExercisesAdded?.(withPerformance);
    },
    [onExercisesAdded, studentId]
  );

  const addExerciseToRoutine = useCallback(
    async (exercise, exerciseType) => {
      const typeId = exerciseType?.id ?? exercise.exercise_type_id;
      const typeName =
        exerciseType?.exercise_type_name ?? exercise.exercise_type_name ?? 'Exercise';
      const newExercise = {
        id: Date.now() + Math.random(),
        exercise_id: exercise.id ?? exercise.exercise_id,
        exercise_name: exercise.exercise_name,
        exercise_type: typeName,
        sets: exercise.sets || 1,
        reps: exercise.reps || 1,
        is_time_based_exercise: exercise.is_time_based_exercise,
        is_height_based: exercise.is_height_based,
        is_weight_based: exercise.is_weight_based,
        time: exercise.time ?? null,
        weight_kg: exercise.weight_kg || null,
        height: exercise.height || null,
        tempo: exercise.tempo || null,
        course_id: selectedCourse?.id,
        course_name: selectedCourse?.course_name,
        exercise_type_id: typeId,
        display_url: exercise.display_url || null,
        display_images: exercise.display_images || null,
        description: exercise.description || null,
      };
      await mergeAndNotify(newExercise);
    },
    [mergeAndNotify, selectedCourse]
  );

  const handleCoursesTilePress = () => {
    if (subMenuActive) {
      flashTileRed(coursesTileColorAnim);
      return;
    }
    closeOtherMenus(showCoursesDropdown ? null : 'courses');
    setShowCoursesDropdown((v) => !v);
  };

  const handleExercisesTilePress = () => {
    if (selectedCourse || selectedExerciseType || showExercisesDropdown || showExerciseTypesDropdown) {
      flashTileRed(exercisesTileColorAnim);
      return;
    }
    closeOtherMenus(showExercisesMenu ? null : 'exercises');
    setShowExercisesMenu((v) => !v);
  };

  const handleGoalsTilePress = async () => {
    if (selectedCourse || selectedExerciseType || showExercisesDropdown || showExerciseTypesDropdown) {
      flashTileRed(goalsTileColorAnim);
      return;
    }
    closeOtherMenus(showGoalsDropdown ? null : 'goals');
    const next = !showGoalsDropdown;
    setShowGoalsDropdown(next);
    if (!next) {
      setExpandedGoalId(null);
      return;
    }
    setLoadingGoals(true);
    try {
      const res = await fetch(`${getApiUrl()}/api/goals`);
      setGoals(res.ok ? await res.json() : []);
    } catch {
      setGoals([]);
    } finally {
      setLoadingGoals(false);
    }
  };

  const handleCourseSelect = async (course) => {
    setExercisePickerSource('course');
    setSelectedCourse(course);
    setShowCoursesDropdown(false);
    try {
      const typesUrl = exerciseTypesByCourseUrl(course.id, {
        viewerId: studentId,
        studentId,
      });
      const res = await fetch(typesUrl);
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setExerciseTypes(Array.isArray(data) ? data : []);
      if (data?.length) setShowExerciseTypesDropdown(true);
      else Alert.alert('No Exercise Types', 'This course has no exercise types available.');
    } catch {
      Alert.alert('Error', 'Failed to load exercise types');
    }
  };

  const handleExerciseTypeSelect = async (exerciseType) => {
    setSelectedExerciseType(exerciseType);
    setShowExerciseTypesDropdown(false);
    setShowExercisesDropdown(true);
    setSelectedExercises(new Set());
    setSelectAllExercises(false);
    try {
      const listUrl = exercisesListUrl({
        exerciseTypeId: exerciseType.id,
        courseId: selectedCourse?.id,
        studentId,
        viewerId: studentId,
      });
      const res = await fetch(listUrl);
      setExercises(res.ok ? await res.json() : []);
    } catch {
      setExercises([]);
    }
  };

  const handleAddSelectedExercises = async () => {
    if (!selectedExerciseType || selectedExercises.size === 0) return;
    const selectedList = exercises.filter((ex) => selectedExercises.has(ex.id));
    const raw = selectedList.map((exercise) => ({
      id: Date.now() + Math.random(),
      exercise_id: exercise.id,
      exercise_name: exercise.exercise_name,
      exercise_type: selectedExerciseType.exercise_type_name,
      sets: exercise.sets || 1,
      reps: exercise.reps || 1,
      is_time_based_exercise: exercise.is_time_based_exercise,
      is_height_based: exercise.is_height_based,
      is_weight_based: exercise.is_weight_based,
      time: exercise.time ?? null,
      weight_kg: exercise.weight_kg || null,
      height: exercise.height || null,
      tempo: exercise.tempo || null,
      course_id: selectedCourse?.id,
      course_name: selectedCourse?.course_name,
      exercise_type_id: selectedExerciseType.id,
      display_url: exercise.display_url || null,
      display_images: exercise.display_images || null,
      description: exercise.description || null,
    }));
    await mergeAndNotify(raw);
    setShowExercisesDropdown(false);
    setShowExerciseTypesDropdown(false);
    setSelectedCourse(null);
    setSelectedExerciseType(null);
    setExercisePickerSource(null);
    setExercises([]);
    setSelectedExercises(new Set());
    setSelectAllExercises(false);
  };

  const handleShowGradeExercises = async () => {
    const next = !showGradeExercises;
    setShowGradeExercises(next);
    if (next && studentId) {
      try {
        const res = await fetch(`${getApiUrl()}/api/exercises/grade/${studentId}`);
        setGradeExercises(res.ok ? await res.json() : []);
      } catch {
        setGradeExercises([]);
      }
    }
  };

  const handleShowAssignedExercises = async () => {
    setExercisePickerSource('assigned');
    setSelectedCourse(null);
    setSelectedExerciseType(null);
    setShowExercisesMenu(false);
    setShowGradeExercises(false);
    setShowExercisesDropdown(false);
    setExercises([]);
    setSelectedExercises(new Set());
    try {
      const res = await fetch(
        nonCourseExerciseTypesUrl({ viewerId: studentId, studentId })
      );
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setExerciseTypes(Array.isArray(data) ? data : []);
      if (data?.length) setShowExerciseTypesDropdown(true);
      else Alert.alert('No Assigned Exercise Types', 'No assigned exercises outside courses.');
    } catch {
      Alert.alert('Error', 'Failed to load assigned exercise types.');
    }
  };

  const handleSelectGoal = async (goal) => {
    try {
      if (!goal?.exercise_id) {
        Alert.alert('No Exercise', 'This goal has no associated exercise.');
        return;
      }
      const res = await fetch(
        exerciseByIdUrl(goal.exercise_id, { studentId, viewerId: studentId })
      );
      if (!res.ok) throw new Error('Failed');
      const exercise = await res.json();
      await mergeAndNotify({
        id: Date.now() + Math.random(),
        exercise_id: exercise.id,
        exercise_name: exercise.exercise_name || goal.exercise_name || goal.goal_name,
        exercise_type: exercise.exercise_type_name || 'Goal Exercise',
        sets: exercise.sets || 1,
        reps: exercise.reps || 1,
        is_time_based_exercise: exercise.is_time_based_exercise,
        is_height_based: exercise.is_height_based,
        is_weight_based: exercise.is_weight_based,
        time: exercise.time ?? null,
        weight_kg: exercise.weight_kg || null,
        height: exercise.height || null,
        tempo: exercise.tempo || null,
        exercise_type_id: exercise.exercise_type_id || `goal-${goal.id}`,
        display_url: exercise.display_url || null,
        display_images: exercise.display_images || null,
        description: exercise.description || null,
      });
      setShowGoalsDropdown(false);
      setExpandedGoalId(null);
    } catch {
      Alert.alert('Error', 'Failed to add goal exercise.');
    }
  };

  const tiles = [
    {
      key: 'courses',
      anim: coursesTileColorAnim,
      image: COURSES_IMAGE,
      label: 'Courses',
      onPress: handleCoursesTilePress,
    },
    {
      key: 'exercises',
      anim: exercisesTileColorAnim,
      image: EXERCISES_IMAGE,
      label: 'Exercises',
      onPress: handleExercisesTilePress,
    },
    {
      key: 'goals',
      anim: goalsTileColorAnim,
      image: GOALS_IMAGE,
      label: 'Goals',
      onPress: handleGoalsTilePress,
    },
  ];

  return (
    <View style={styles.wrap}>
      <Text style={styles.pickerHint}>Add exercises using Courses, Exercises, or Goals:</Text>
      <View style={styles.tilesRow}>
        {tiles.map((t) => (
          <Animated.View
            key={t.key}
            style={[
              styles.tile,
              {
                backgroundColor: t.anim.interpolate({
                  inputRange: [0, 1],
                  outputRange: ['#ff0000', theme.isDarkMode ? theme.colors.surface : '#FFFFFF'],
                }),
              },
            ]}
          >
            <Image source={t.image} style={styles.tileImage} contentFit="contain" />
            <TouchableOpacity style={styles.tileButton} onPress={t.onPress} activeOpacity={0.7}>
              <Text style={styles.tileButtonText} numberOfLines={2}>
                {t.label}
              </Text>
            </TouchableOpacity>
          </Animated.View>
        ))}
      </View>

      {showCoursesDropdown ? (
        <View style={styles.dropdown}>
          {courses.map((course) => (
            <TouchableOpacity
              key={course.id}
              style={styles.dropdownItem}
              onPress={() => handleCourseSelect(course)}
            >
              <Text style={styles.dropdownItemText}>{course.course_name}</Text>
            </TouchableOpacity>
          ))}
        </View>
      ) : null}

      {showExercisesMenu ? (
        <View style={styles.dropdown}>
          <TouchableOpacity style={styles.menuHeader} onPress={handleShowGradeExercises}>
            <Text style={styles.menuHeaderText}>Grade Exercises</Text>
            <Text style={styles.menuArrow}>{showGradeExercises ? '▼' : '▶'}</Text>
          </TouchableOpacity>
          {showGradeExercises ? (
            <View style={styles.exerciseGrid}>
              {gradeExercises.map((exercise) => (
                <TouchableOpacity
                  key={exercise.id}
                  style={styles.exerciseTile}
                  onPress={() => addExerciseToRoutine(exercise, {
                    id: exercise.exercise_type_id,
                    exercise_type_name: exercise.exercise_type_name || 'Grade Exercise',
                  })}
                >
                  <Text style={styles.exerciseName}>{exercise.exercise_name}</Text>
                </TouchableOpacity>
              ))}
            </View>
          ) : null}
          <TouchableOpacity style={styles.menuHeader} onPress={handleShowAssignedExercises}>
            <Text style={styles.menuHeaderText}>Assigned Exercises</Text>
            <Text style={styles.menuArrow}>▶</Text>
          </TouchableOpacity>
        </View>
      ) : null}

      {showExerciseTypesDropdown && exerciseTypes.length > 0 ? (
        <View style={styles.dropdown}>
          <Text style={styles.dropdownTitle}>
            {exercisePickerSource === 'assigned'
              ? 'Select Assigned Exercise Type:'
              : 'Select Exercise Type:'}
          </Text>
          {exerciseTypes.map((exerciseType) => (
            <TouchableOpacity
              key={exerciseType.id}
              style={styles.dropdownItem}
              onPress={() => handleExerciseTypeSelect(exerciseType)}
            >
              <Text style={styles.dropdownItemText}>{exerciseType.exercise_type_name}</Text>
            </TouchableOpacity>
          ))}
        </View>
      ) : null}

      {showExercisesDropdown && exercises.length > 0 ? (
        <View style={styles.dropdown}>
          <Text style={styles.dropdownTitle}>Select Exercises:</Text>
          <TouchableOpacity style={styles.selectAllRow} onPress={() => {
            if (selectAllExercises) {
              setSelectedExercises(new Set());
              setSelectAllExercises(false);
            } else {
              setSelectedExercises(new Set(exercises.map((ex) => ex.id)));
              setSelectAllExercises(true);
            }
          }}>
            <Text style={styles.selectAllText}>
              {selectAllExercises ? '☑' : '☐'} Select all
            </Text>
          </TouchableOpacity>
          {exercises.map((exercise) => {
            const checked = selectedExercises.has(exercise.id);
            return (
              <TouchableOpacity
                key={exercise.id}
                style={styles.dropdownItem}
                onPress={() => {
                  const next = new Set(selectedExercises);
                  if (next.has(exercise.id)) next.delete(exercise.id);
                  else next.add(exercise.id);
                  setSelectedExercises(next);
                  setSelectAllExercises(next.size === exercises.length);
                }}
              >
                <Text style={styles.dropdownItemText}>
                  {checked ? '☑' : '☐'} {exercise.exercise_name}
                </Text>
              </TouchableOpacity>
            );
          })}
          <TouchableOpacity
            style={[styles.addButton, selectedExercises.size === 0 && styles.addButtonDisabled]}
            onPress={handleAddSelectedExercises}
            disabled={selectedExercises.size === 0}
          >
            <Text style={styles.addButtonText}>Add ({selectedExercises.size})</Text>
          </TouchableOpacity>
        </View>
      ) : null}

      {showGoalsDropdown ? (
        <View style={styles.dropdown}>
          {loadingGoals ? (
            <Text style={styles.dropdownItemText}>Loading goals…</Text>
          ) : goals.length === 0 ? (
            <Text style={styles.dropdownItemText}>No goals found.</Text>
          ) : (
            goals.map((goal) => (
              <View key={goal.id}>
                <TouchableOpacity
                  style={styles.dropdownItem}
                  onPress={() =>
                    setExpandedGoalId((prev) => (prev === goal.id ? null : goal.id))
                  }
                >
                  <Text style={styles.dropdownItemText}>{goal.goal_name || goal.name}</Text>
                </TouchableOpacity>
                {expandedGoalId === goal.id ? (
                  <TouchableOpacity
                    style={styles.goalSelectBtn}
                    onPress={() => handleSelectGoal(goal)}
                  >
                    <Text style={styles.goalSelectBtnText}>Select</Text>
                  </TouchableOpacity>
                ) : null}
              </View>
            ))
          )}
        </View>
      ) : null}
    </View>
  );
}

const getStyles = (theme) =>
  StyleSheet.create({
    wrap: { marginTop: 12, backgroundColor: 'transparent' },
    pickerHint: {
      fontSize: 13,
      color: theme.colors.text,
      opacity: 0.85,
      marginBottom: 10,
      backgroundColor: 'transparent',
    },
    tilesRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
      gap: 8,
    },
    tile: {
      width: (width - 56) / 3,
      minWidth: 100,
      borderRadius: 8,
      borderWidth: 2,
      borderColor: theme.colors.primary || '#d4af37',
      padding: 8,
      alignItems: 'center',
      marginBottom: 8,
    },
    tileImage: { width: '100%', height: 56, marginBottom: 6 },
    tileButton: {
      backgroundColor: theme.colors.primary || '#d4af37',
      borderRadius: 6,
      paddingVertical: 6,
      paddingHorizontal: 8,
      width: '100%',
      alignItems: 'center',
    },
    tileButtonText: {
      color: '#FFFFFF',
      fontWeight: '600',
      fontSize: 12,
      textAlign: 'center',
    },
    dropdown: {
      marginTop: 8,
      marginBottom: 8,
      padding: 12,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: theme.colors.border,
      backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
    },
    dropdownTitle: {
      fontSize: 14,
      fontWeight: '600',
      color: theme.colors.text,
      marginBottom: 8,
    },
    dropdownItem: {
      paddingVertical: 10,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: theme.colors.border,
    },
    dropdownItemText: { fontSize: 14, color: theme.colors.text },
    menuHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      paddingVertical: 12,
    },
    menuHeaderText: { fontSize: 15, fontWeight: '600', color: theme.colors.text },
    menuArrow: { fontSize: 14, color: theme.colors.text },
    exerciseGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 8 },
    exerciseTile: {
      padding: 10,
      borderRadius: 6,
      borderWidth: 1,
      borderColor: theme.colors.border,
      minWidth: '45%',
    },
    exerciseName: { fontSize: 13, color: theme.colors.text },
    selectAllRow: { paddingVertical: 8, marginBottom: 4 },
    selectAllText: { fontSize: 14, color: theme.colors.text, fontWeight: '600' },
    addButton: {
      marginTop: 10,
      backgroundColor: theme.colors.primary || '#d4af37',
      borderRadius: 8,
      paddingVertical: 12,
      alignItems: 'center',
    },
    addButtonDisabled: { opacity: 0.5 },
    addButtonText: { color: '#FFFFFF', fontWeight: '600', fontSize: 15 },
    goalSelectBtn: {
      marginLeft: 12,
      marginBottom: 8,
      alignSelf: 'flex-start',
      backgroundColor: theme.colors.primary || '#d4af37',
      borderRadius: 6,
      paddingVertical: 6,
      paddingHorizontal: 14,
    },
    goalSelectBtnText: { color: '#FFFFFF', fontWeight: '600' },
  });
